/** @file Converts one rig timeline (JSON arrays, names, hex colours) into compiled TimelineData. */
import { assertNever } from '@pose2d/std/assertNever';
import type { TimelineJson } from '@pose2d/format/types';
import type { EventData, TimelineData } from '../../skeletonData';
import { coreFail } from '../coreFail';
import type { Interner } from './createInterner';
import { indexIn } from './indexIn';
import { packHex } from './packHex';
import { readCurves } from './readCurves';

/** Entity counts that timeline indices are checked against; `deformSizes[i]` = deformSize(attachments[i]) (W2-03). */
export interface TimelineCounts {
  bones: number;
  slots: number;
  ik: number;
  events: number;
  deformSizes: readonly number[];
}

type EventTimelineJson = Extract<TimelineJson, { k: 'event' }>;
type DeformTimelineJson = Extract<TimelineJson, { k: 'deform' }>;
type DeformTimelineData = Extract<TimelineData, { k: 'deform' }>;

/** The list itself when it has one entry per key; VALIDATION at path otherwise. */
function sized<T>(list: readonly T[], keys: number, path: string): readonly T[] {
  return list.length === keys ? list : coreFail('VALIDATION', `${list.length} entries for ${keys} keys`, { path });
}

/**
 * Key times in seconds: every entry a finite number >= 0 and the list strictly ascending (equal times only when
 * `allowEqual`, for event timelines, where several events may share a frame); VALIDATION at `<path>[i]` otherwise.
 * Descending, duplicate or NaN times would make the samplers pick the wrong segment or divide by zero.
 */
function readTimes(t: readonly unknown[], allowEqual: boolean, path: string): Float64Array {
  const times = new Float64Array(t.length);
  let previous = Number.NEGATIVE_INFINITY;
  t.forEach((time, i) => {
    if (typeof time !== 'number' || !Number.isFinite(time) || time < 0) {
      coreFail('VALIDATION', `key time ${String(time)} must be a finite number of seconds, 0 or more`, { path: `${path}[${i}]` });
    }
    if (allowEqual ? time < previous : time <= previous) {
      coreFail('VALIDATION', `key time ${time} does not come ${allowEqual ? 'at or ' : ''}after the previous key time ${previous}`, { path: `${path}[${i}]` });
    }
    times[i] = time;
    previous = time;
  });
  return times;
}

/** A full draw-order permutation: every slot exactly once (VALIDATION at path otherwise). */
function readPermutation(order: readonly number[], slots: number, path: string): Int32Array {
  if (order.length !== slots) coreFail('VALIDATION', `draw order lists ${order.length} slots; the rig has ${slots}`, { path });
  const seen = new Uint8Array(slots);
  return Int32Array.from(order, (s) => {
    const slot = indexIn(s, slots, path);
    if (seen[slot] === 1) coreFail('VALIDATION', `slot ${slot} appears twice in the draw order`, { path });
    seen[slot] = 1;
    return slot;
  });
}

/** Deform keys of a mesh: `start[k]` in [0, size] and `start[k] + v[k].length <= size`, size = deformSize. */
function readDeform(tl: DeformTimelineJson, keys: number, times: Float64Array, counts: TimelineCounts, path: string): DeformTimelineData {
  const attachment = indexIn(tl.att, counts.deformSizes.length, `${path}.att`);
  const size = counts.deformSizes[attachment] ?? 0;
  if (size === 0) coreFail('VALIDATION', `attachment ${attachment} is not a mesh; only meshes deform`, { path: `${path}.att` });
  const starts = Int32Array.from(sized(tl.start, keys, `${path}.start`), (s, k) => indexIn(s, size + 1, `${path}.start[${k}]`));
  const values = sized(tl.v, keys, `${path}.v`).map((v, k) => {
    if ((starts[k] ?? 0) + v.length > size) coreFail('VALIDATION', `deform key ends past the mesh's ${size} numbers`, { path: `${path}.v[${k}]` });
    return Float64Array.from(v);
  });
  return { k: 'deform', attachment, times, starts, values, curves: readCurves(tl.c, keys, `${path}.c`) };
}

/** Per-key overrides (each given list has one entry per key); a key whose int, float, string are all null gets null. */
function readOverrides(tl: EventTimelineJson, path: string): (Partial<EventData> | null)[] {
  const keys = tl.t.length;
  const ints = tl.int && sized(tl.int, keys, `${path}.int`);
  const floats = tl.float && sized(tl.float, keys, `${path}.float`);
  const strings = tl.string && sized(tl.string, keys, `${path}.string`);
  return tl.t.map((_, i): Partial<EventData> | null => {
    const int = ints?.[i] ?? null;
    const float = floats?.[i] ?? null;
    const string = strings?.[i] ?? null;
    if (int === null && float === null && string === null) return null;
    return { ...(int === null ? {} : { int }), ...(float === null ? {} : { float }), ...(string === null ? {} : { string }) };
  });
}

/** Every kind this runtime reads; the Record type makes TypeScript demand an entry for each kind of the schema. */
const READ_KINDS: Record<TimelineJson['k'], true> = {
  rotation: true, x: true, y: true, scaleX: true, scaleY: true, shearX: true, shearY: true, inherit: true, attachment: true,
  rgb: true, dark: true, alpha: true, ikMix: true, ikSoftness: true, ikBend: true, ikStretch: true, ikCompress: true,
  deform: true, drawOrder: true, event: true,
};

/**
 * Compiled timeline, or null for a kind this runtime does not know (a newer minor, §5.5): an unknown STRING kind is
 * skipped before any other field (times included) is read, so it can never be rejected for its own shape. A missing
 * or non-string `k` is not a newer kind but a malformed timeline: VALIDATION at `<path>.k`.
 */
export function readTimeline(tl: TimelineJson, counts: TimelineCounts, placeholders: Interner, path: string): TimelineData | null {
  const kind: unknown = tl.k;
  if (typeof kind !== 'string') coreFail('VALIDATION', `timeline kind must be a string, got ${String(kind)}`, { path: `${path}.k` });
  if (!Object.hasOwn(READ_KINDS, kind)) return null;
  const keys = tl.t.length;
  const times = readTimes(tl.t, tl.k === 'event', `${path}.t`);
  const vPath = `${path}.v`;
  switch (tl.k) {
    case 'rotation': case 'x': case 'y': case 'scaleX': case 'scaleY': case 'shearX': case 'shearY':
      return {
        k: tl.k, bone: indexIn(tl.bone, counts.bones, `${path}.bone`), times,
        values: Float64Array.from(sized(tl.v, keys, vPath)), curves: readCurves(tl.c, keys, `${path}.c`),
      };
    case 'inherit':
      return {
        k: 'inherit', bone: indexIn(tl.bone, counts.bones, `${path}.bone`), times,
        values: Uint8Array.from(sized(tl.v, keys, vPath), (v, i) => indexIn(v, 5, `${vPath}[${i}]`)),
      };
    case 'attachment':
      return {
        k: 'attachment', slot: indexIn(tl.slot, counts.slots, `${path}.slot`), times,
        values: Int32Array.from(sized(tl.v, keys, vPath), (v) => (v === null ? -1 : placeholders.intern(v))),
      };
    case 'rgb': case 'dark':
      return {
        k: tl.k, slot: indexIn(tl.slot, counts.slots, `${path}.slot`), times,
        values: Uint32Array.from(sized(tl.v, keys, vPath), (v, i) => packHex(v, `${vPath}[${i}]`)), curves: readCurves(tl.c, keys, `${path}.c`),
      };
    case 'alpha':
      return {
        k: 'alpha', slot: indexIn(tl.slot, counts.slots, `${path}.slot`), times,
        values: Float64Array.from(sized(tl.v, keys, vPath)), curves: readCurves(tl.c, keys, `${path}.c`),
      };
    case 'ikMix': case 'ikSoftness':
      return {
        k: tl.k, ik: indexIn(tl.ik, counts.ik, `${path}.ik`), times,
        values: Float64Array.from(sized(tl.v, keys, vPath)), curves: readCurves(tl.c, keys, `${path}.c`),
      };
    case 'ikBend': case 'ikStretch': case 'ikCompress':
      return {
        k: tl.k, ik: indexIn(tl.ik, counts.ik, `${path}.ik`), times,
        values: Uint8Array.from(sized(tl.v, keys, vPath), (v) => (v ? 1 : 0)),
      };
    case 'deform':
      return readDeform(tl, keys, times, counts, path);
    case 'drawOrder':
      return {
        k: 'drawOrder', times,
        values: sized(tl.v, keys, vPath).map((v, i) => (v === null ? null : readPermutation(v, counts.slots, `${vPath}[${i}]`))),
      };
    case 'event':
      return {
        k: 'event', times,
        events: Int32Array.from(sized(tl.e, keys, `${path}.e`), (e, i) => indexIn(e, counts.events, `${path}.e[${i}]`)),
        overrides: readOverrides(tl, path),
      };
    default:
      return assertNever(tl);
  }
}
